import { Typography, styled } from "@mui/material";

import { StarburstIcon } from "@citrine/atoms/icons";
import { Row } from "@citrine/atoms/layout";

// The user's turn, shared by both Catalyst flows (agent + knowledge/model): a
// compact grey bubble that right-aligns itself within the flex-column thread.
// Rendered via the <UserBubble> component (chat.tsx), which pairs it with the
// break-word body text below.
export const UserBubbleRoot = styled("div")(({ theme }) => ({
  alignSelf: "flex-end",
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.shape.borderRadiusLg,
  color: theme.palette.text.primary,
  maxWidth: "85%",
  padding: theme.spacing(1, 1.5),
  whiteSpace: "pre-wrap",
}));

// body1 text that breaks long unbroken strings (URLs, IDs) rather than
// overflowing the bubble.
export const UserBubbleText = styled(Typography)({ wordBreak: "break-word" });

// The input bar shared by both flows: a row holding the question field + send
// button, pinned below the message thread.
export const InputBar = styled(Row)(({ theme }) => ({
  background: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
}));

// The teal agent glyph that ties Catalyst's status vocabulary together: event
// lines, mode-change statements, and the streaming cue all share this icon.
export const AgentGlyph = styled(StarburstIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
  fontSize: theme.spacing(1.75),
}));

// While the assistant is working, the agent glyph pulses — scaling out from its
// center while fading — to signal "working", in place of a blinking caret.
// Extends AgentGlyph so the icon/color stay identical to the event line.
export const StreamingIndicator = styled(AgentGlyph)(({ theme }) => ({
  animation: "pulse 1.5s ease-in-out infinite",
  // Inherit AgentGlyph's box (no fontSize override) so the cue stays aligned —
  // same left edge and center — with the static glyph column. The "active
  // cursor" emphasis comes from the pulse scaling up to the glyph's larger 24px
  // size (transformOrigin: center, so it never shifts the layout).
  // Separate it from the content above.
  marginTop: theme.spacing(2),
  transformOrigin: "center",
  verticalAlign: "text-bottom",

  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.4, transform: "scale(1)" },
    "50%": { opacity: 1, transform: "scale(calc(24 / 14))" },
  },

  // Hold the glyph steady for users who opt out of motion.
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));
